import {useDataSource} from "./DataSource";
import {Tab, Tabs} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import Loading from "@/components/breadcrumbs/Loading";
import UpdateIvrProfileForm from "./UpdateIvrProfileForm";
import {useDispatch} from "react-redux";
import {formPagesWidth} from "@/constants";
import {showNotification} from "@/actions";
import {FormattedMessage, defineMessage} from "react-intl";
import {Colors} from "@/styles";
import TabPanel from "@/components/extension/devices/TabPanel";
import {useActivateIvrProfileMutation} from "./mutation.generated";
import {useState} from "react";

const useStyles = makeStyles()({
    activeProfileTag: {
        marginLeft: 5,
        fontSize: "0.6em",
        backgroundColor: Colors.Base,
        color: "white",
        padding: "1px 3px 0px",
        borderRadius: 3,
    },
});

const TabLabel = ({name, active}: {name: string; active: boolean}) => {
    const {classes} = useStyles();

    return (
        <div style={{display: "flex", alignItems: "center"}}>
            {name}
            {active && (
                <span className={classes.activeProfileTag}>
                    <FormattedMessage id="active" defaultMessage="active" />
                </span>
            )}
        </div>
    );
};

type Props = {
    domainId: number;
    ivrId: number;
};

const UpdateIvrProfilesPage = ({domainId, ivrId}: Props) => {
    const dataSource = useDataSource(ivrId, domainId);
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState(0);
    const [activateProfileMutation] = useActivateIvrProfileMutation();
    const [activating, setActivating] = useState(false);

    if (dataSource.loading) {
        return <Loading />;
    }

    const profiles = dataSource.ivr.profiles;

    const profileActivator = (profileId: number) => async () => {
        setActivating(true);
        await activateProfileMutation({variables: {id: profileId}});
        await dataSource.refetch(); // to refresh which profile is active
        dispatch(
            showNotification(
                defineMessage({
                    id: "ivr.notification.profile_activated",
                    defaultMessage: "Auto attendant profile activated",
                }),
            ),
        );
        setActivating(false);
    };

    return (
        <div style={{maxWidth: formPagesWidth + 600}}>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth" indicatorColor="primary">
                {profiles.map((p) => (
                    <Tab
                        key={p.id}
                        label={<TabLabel name={p.name} active={p.id === dataSource.activeProfile} />}
                        style={{borderBottom: `2px solid #ddd`}}
                    />
                ))}
            </Tabs>
            {profiles.map((p, index) => (
                <TabPanel value={activeTab} index={index} key={p.id}>
                    <UpdateIvrProfileForm
                        domainId={domainId}
                        key={`profile-${p.id}`}
                        profileId={p.id}
                        activate={profileActivator(p.id)}
                        isActive={p.id === dataSource.activeProfile}
                        otherProfiles={profiles.filter((profile) => profile.id != p.id)}
                        dataSource={dataSource}
                        activating={activating}
                    />
                </TabPanel>
            ))}
        </div>
    );
};

export default UpdateIvrProfilesPage;
